import { useEffect, useState } from 'react'
import { useForm } from 'react-hook-form'
import Dialog from '@mui/material/Dialog'
import DialogTitle from '@mui/material/DialogTitle'
import DialogContent from '@mui/material/DialogContent'
import DialogActions from '@mui/material/DialogActions'
import TextField from '@mui/material/TextField'
import Button from '@mui/material/Button'
import Box from '@mui/material/Box'
import Alert from '@mui/material/Alert'
import Typography from '@mui/material/Typography'
import { getBackendError } from '../../../shared/utils/formErrors'

interface FormData {
    passwordActual: string
    novaPassword: string
    confirmar: string
}

interface Props {
    open: boolean
    title: string
    subtitle?: string
    /** Mostra o campo "Palavra-passe actual" (mudança da própria palavra-passe). */
    requireCurrent?: boolean
    onClose: () => void
    onSaved: () => void
    submit: (values: { passwordActual?: string; novaPassword: string }) => Promise<void>
}

export default function PasswordDialog({ open, title, subtitle, requireCurrent, onClose, onSaved, submit }: Props) {
    const [apiError, setApiError] = useState('')
    const [submitting, setSubmitting] = useState(false)

    const { register, handleSubmit, reset, watch, formState: { errors } } = useForm<FormData>({
        defaultValues: { passwordActual: '', novaPassword: '', confirmar: '' },
    })

    useEffect(() => {
        if (open) {
            setApiError('')
            reset({ passwordActual: '', novaPassword: '', confirmar: '' })
        }
    }, [open, reset])

    const novaPassword = watch('novaPassword')

    const onSubmit = async (data: FormData) => {
        setApiError('')
        setSubmitting(true)
        try {
            await submit({
                passwordActual: requireCurrent ? data.passwordActual : undefined,
                novaPassword: data.novaPassword,
            })
            onSaved()
        } catch (err) {
            setApiError(getBackendError(err)?.message ?? 'Não foi possível alterar a palavra-passe.')
        } finally {
            setSubmitting(false)
        }
    }

    return (
        <Dialog open={open} onClose={submitting ? undefined : onClose} maxWidth="xs" fullWidth>
            <Box component="form" onSubmit={handleSubmit(onSubmit)} noValidate>
                <DialogTitle sx={{ fontWeight: 700 }}>{title}</DialogTitle>
                <DialogContent sx={{ display: 'flex', flexDirection: 'column', gap: 2.5 }}>
                    {subtitle && <Typography variant="body2" color="text.secondary">{subtitle}</Typography>}
                    {apiError && <Alert severity="error" sx={{ borderRadius: 2 }}>{apiError}</Alert>}

                    {requireCurrent && (
                        <TextField
                            label="Palavra-passe actual"
                            type="password"
                            autoComplete="current-password"
                            autoFocus
                            error={!!errors.passwordActual}
                            helperText={errors.passwordActual?.message}
                            {...register('passwordActual', { required: 'Introduza a palavra-passe actual.' })}
                        />
                    )}
                    <TextField
                        label="Nova palavra-passe"
                        type="password"
                        autoComplete="new-password"
                        autoFocus={!requireCurrent}
                        error={!!errors.novaPassword}
                        helperText={errors.novaPassword?.message ?? 'Mínimo 8 caracteres.'}
                        {...register('novaPassword', {
                            required: 'Introduza a nova palavra-passe.',
                            minLength: { value: 8, message: 'Mínimo 8 caracteres.' },
                        })}
                    />
                    <TextField
                        label="Confirmar nova palavra-passe"
                        type="password"
                        autoComplete="new-password"
                        error={!!errors.confirmar}
                        helperText={errors.confirmar?.message}
                        {...register('confirmar', {
                            required: 'Confirme a nova palavra-passe.',
                            validate: (v) => v === novaPassword || 'As palavras-passe não coincidem.',
                        })}
                    />
                </DialogContent>
                <DialogActions sx={{ px: 3, pb: 2 }}>
                    <Button onClick={onClose} disabled={submitting}>Cancelar</Button>
                    <Button type="submit" variant="contained" disabled={submitting}>
                        {submitting ? 'A guardar…' : 'Guardar'}
                    </Button>
                </DialogActions>
            </Box>
        </Dialog>
    )
}
